import React from "react";
import "./DashboardLoanTermsCard.css";
import { useTranslation } from "react-i18next";

/* =========================================================
   HELPERS
   ========================================================= */

function firstDefined(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
  }

  return null;
}

function parseNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const normalized =
    typeof value === "string" ? value.replace(/,/g, "") : value;

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
}

function formatEuro(value) {
  const amount = parseNumber(value);

  if (amount == null) {
    return "—";
  }

  return `€${amount.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatPercent(value) {
  const amount = parseNumber(value);

  if (amount == null) {
    return "—";
  }

  return `${amount.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
}

function formatYears(value, t) {
  const years = parseNumber(value);

  if (years == null) {
    return "—";
  }

  return `${years.toLocaleString("en-US", {
    maximumFractionDigits: 0,
  })} ${t("loanTerms.years", "years")}`;
}

function getFundingStatusLabel(status, t) {
  switch (String(status || "").toUpperCase()) {
    case "CONFIRMED":
      return t("loanTerms.confirmed", "Confirmed");

    case "EXPECTED":
      return t("loanTerms.expected", "Expected");

    default:
      return status || "—";
  }
}

function getFundingTypeLabel(sourceType, t) {
  switch (String(sourceType || "").toUpperCase()) {
    case "SUBSIDY":
    case "GRANT":
      return t("loanTerms.fundingTypes.subsidy", "Subsidy");

    case "SOFT_LOAN":
      return t("loanTerms.fundingTypes.softLoan", "Soft loan");

    case "OTHER":
      return t("loanTerms.fundingTypes.other", "Other funding");

    default:
      return t("loanTerms.fundingTypes.other", "Other funding");
  }
}

/* =========================================================
   COMPONENT
   ========================================================= */

export default function DashboardLoanTermsCard({ summary = {} }) {
  const { t } = useTranslation("homebuyer-dashboard");

  /* =======================================================
     LENDER
     ======================================================= */

  const lenderName = firstDefined(
    summary.lender_name,
    summary.lender_organisation_name,
  );

  /* =======================================================
     APPROVED LENDER LOAN
     ======================================================= */

  const approvedTotalLoanAmount = firstDefined(
    summary.approved_total_loan_amount,

    summary.agreed_total_loan_amount,

    summary.total_loan_amount,
  );

  const approvedPurchaseLoanAmount = firstDefined(
    summary.approved_purchase_loan_amount,

    summary.agreed_purchase_loan_amount,

    summary.purchase_loan_amount,
  );

  const approvedRenovationLoanAmount = firstDefined(
    summary.approved_renovation_loan_amount,

    summary.agreed_renovation_loan_amount,

    summary.renovation_loan_amount,
  );

  /* =======================================================
     OTHER FINANCING TERMS
     ======================================================= */

  const ownContribution = firstDefined(
    summary.own_contribution_amount,

    summary.agreed_own_contribution_amount,
  );

  const monthlyPayment = firstDefined(
    summary.estimated_monthly_payment,

    summary.agreed_monthly_payment,
  );

  const tenureYears = firstDefined(
    summary.approved_tenure_years,

    summary.tenure_years,

    summary.agreed_tenure_years,

    summary.loan_term_years,
  );

  const indicativeApr = firstDefined(
    summary.indicative_apr_pct,

    summary.estimated_apr,

    summary.agreed_apr,
  );

  /* =======================================================
     DECISION
     ======================================================= */

  const decision = String(
    firstDefined(
      summary.loan_decision,
      summary.lender_result_status,
      summary.decision,
      "",
    ),
  ).toUpperCase();

  const isApproved =
    decision === "APPROVED" || Boolean(summary.has_agreed_lender_result);

  /* =======================================================
     RENOVATION FINANCING STATUS
     ======================================================= */

  const renovationFinancingStatus = String(
    firstDefined(
      summary.renovation_financing_status,

      summary.loan_result_renovation_financing_status,

      "ESTIMATED",
    ),
  ).toUpperCase();

  const renovationFinancingNotes = firstDefined(
    summary.renovation_financing_notes,

    summary.loan_result_renovation_financing_notes,
  );

  const isRenovationConfirmed = renovationFinancingStatus === "CONFIRMED";

  /* =======================================================
     ADDITIONAL LENDER RENOVATION LOANS

     The INITIAL loan case remains the base lender decision.
     ADDITIONAL cases are child financing records and must be
     added on top of the original lender terms, never replace
     them.
     ======================================================= */

  const additionalRenovationLoans = Array.isArray(
    summary.additional_renovation_loans,
  )
    ? summary.additional_renovation_loans.filter((loan) => {
        const decision = String(loan?.decision || "APPROVED").toUpperCase();
        return decision === "APPROVED";
      })
    : [];

  const additionalRenovationLoanTotal = additionalRenovationLoans.reduce(
    (total, loan) =>
      total +
      (parseNumber(
        firstDefined(
          loan?.approved_renovation_loan_amount,
          loan?.approved_total_loan_amount,
          loan?.amount,
        ),
      ) || 0),
    0,
  );

  const additionalMonthlyPaymentTotal = additionalRenovationLoans.reduce(
    (total, loan) =>
      total + (parseNumber(loan?.estimated_monthly_payment) || 0),
    0,
  );

  const baseApprovedTotalLoan = parseNumber(approvedTotalLoanAmount) || 0;
  const baseMonthlyPayment = parseNumber(monthlyPayment) || 0;

  const combinedApprovedTotalLoan =
    baseApprovedTotalLoan + additionalRenovationLoanTotal;

  const combinedMonthlyPayment =
    baseMonthlyPayment + additionalMonthlyPaymentTotal;

  /* =======================================================
     ADDITIONAL RENOVATION FUNDING
     ======================================================= */

  const rawFundingSources = Array.isArray(summary.funding_sources)
    ? summary.funding_sources
    : Array.isArray(summary.loan_result_funding_sources)
      ? summary.loan_result_funding_sources
      : [];

  /*
   * The lender loan and own contribution are already
   * represented separately on the card.
   *
   * These rows therefore contain only supplementary
   * renovation funding.
   */
  const additionalRenovationFunding = rawFundingSources.filter((source) => {
    const type = String(source?.source_type || "").toUpperCase();

    return ["SUBSIDY", "GRANT", "SOFT_LOAN", "OTHER"].includes(type);
  });

  /* =======================================================
     RENOVATION TOTALS
     ======================================================= */

  const initialLenderRenovationLoan =
    parseNumber(approvedRenovationLoanAmount) || 0;

  const combinedLenderRenovationLoan =
    initialLenderRenovationLoan + additionalRenovationLoanTotal;

  const confirmedAdditionalLenderLoans = additionalRenovationLoans.reduce(
    (total, loan) => {
      const status = String(
        loan?.renovation_financing_status || "CONFIRMED",
      ).toUpperCase();

      if (status !== "CONFIRMED") {
        return total;
      }

      return (
        total +
        (parseNumber(
          firstDefined(
            loan?.approved_renovation_loan_amount,
            loan?.approved_total_loan_amount,
            loan?.amount,
          ),
        ) || 0)
      );
    },
    0,
  );

  const additionalFundingTotal = additionalRenovationFunding.reduce(
    (total, source) => total + (parseNumber(source?.amount) || 0),
    0,
  );

  const confirmedAdditionalFunding = additionalRenovationFunding.reduce(
    (total, source) => {
      const status = String(source?.status || "").toUpperCase();

      if (status !== "CONFIRMED") {
        return total;
      }

      return total + (parseNumber(source?.amount) || 0);
    },
    0,
  );

  const confirmedInitialLenderRenovation = isRenovationConfirmed
    ? initialLenderRenovationLoan
    : 0;

  const confirmedRenovationFunding =
    confirmedInitialLenderRenovation +
    confirmedAdditionalLenderLoans +
    confirmedAdditionalFunding;

  const potentialRenovationFunding =
    combinedLenderRenovationLoan + additionalFundingTotal;

  const hasExpectedExternalFunding = additionalRenovationFunding.some(
    (source) => String(source?.status || "").toUpperCase() === "EXPECTED",
  );

  const hasUnconfirmedAdditionalLenderLoan = additionalRenovationLoans.some(
    (loan) =>
      String(loan?.renovation_financing_status || "CONFIRMED").toUpperCase() !==
      "CONFIRMED",
  );

  const hasUnconfirmedRenovationFunding =
    (initialLenderRenovationLoan > 0 && !isRenovationConfirmed) ||
    hasUnconfirmedAdditionalLenderLoan ||
    hasExpectedExternalFunding;

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <div className="hb-terms-card">
      {/* ===================================================
          HEADER
          =================================================== */}

      <div className="hb-terms-card__header">
        <div>
          <span className="hb-terms-card__eyebrow">
            {t("loanTerms.finalDecision", "Lender decision")}
          </span>

          <h3>
            {isApproved
              ? t("loanTerms.approvedTitle", "Approved financing")
              : t("loanTerms.title", "Financing terms")}
          </h3>
        </div>

        {isApproved ? (
          <span className="hb-terms-card__status">
            {t("loanTerms.approved", "Approved")}
          </span>
        ) : null}
      </div>

      {/* ===================================================
          LENDER
          =================================================== */}

      <div className="hb-terms-card__lender">
        <span>{t("loanTerms.lender", "Lender")}</span>

        <strong>{lenderName || "—"}</strong>
      </div>

      {/* ===================================================
          MAIN VALUES
          =================================================== */}

      <div className="hb-terms-card__amounts">
        <div className="hb-terms-card__primary-amount">
          <span>
            {additionalRenovationLoans.length
              ? t("loanTerms.totalLenderFinancing", "Total lender financing")
              : t("loanTerms.approvedTotal", "Approved total loan")}
          </span>

          <strong>{formatEuro(combinedApprovedTotalLoan)}</strong>

          <small>
            {t(
              "loanTerms.purchaseAndRenovation",
              "Purchase + renovation financing",
            )}
          </small>
        </div>

        <div className="hb-terms-card__monthly">
          <span>
            {t("loanTerms.monthlyPayment", "Estimated monthly payment")}
          </span>

          <strong>{formatEuro(combinedMonthlyPayment)}</strong>

          <small>
            {t(
              "loanTerms.monthlyPaymentNote",
              additionalRenovationLoans.length
                ? "Combined estimated monthly payments"
                : "Based on the approved financing terms",
            )}
          </small>
        </div>
      </div>

      {/* ===================================================
          LENDER LOAN BREAKDOWN
          =================================================== */}

      <section className="hb-terms-section">
        <div className="hb-terms-section__header">
          <div>
            <span>
              {t("loanTerms.lenderLoanBreakdown", "Approved lender loan")}
            </span>

            <small>
              {t(
                "loanTerms.lenderLoanBreakdownSubtitle",
                "Financing provided directly by the lender",
              )}
            </small>
          </div>
        </div>

        <div className="hb-terms-section__rows">
          <div className="hb-terms-funding-row">
            <div className="hb-terms-funding-row__main">
              <span>{t("loanTerms.purchaseFinancing", "Purchase loan")}</span>

              <strong>{formatEuro(approvedPurchaseLoanAmount)}</strong>
            </div>

            <span className="hb-funding-status hb-funding-status--approved">
              {t("loanTerms.approved", "Approved")}
            </span>
          </div>

          <div className="hb-terms-funding-row">
            <div className="hb-terms-funding-row__main">
              <span>
                {additionalRenovationLoans.length
                  ? t(
                      "loanTerms.initialLenderRenovationLoan",
                      "Initial lender renovation loan",
                    )
                  : t(
                      "loanTerms.lenderRenovationLoan",
                      "Lender renovation loan",
                    )}
              </span>

              <strong>{formatEuro(approvedRenovationLoanAmount)}</strong>
            </div>

            <span
              className={`hb-funding-status ${
                isRenovationConfirmed
                  ? "hb-funding-status--confirmed"
                  : "hb-funding-status--estimated"
              }`}
            >
              {isRenovationConfirmed
                ? t("loanTerms.confirmed", "Confirmed")
                : t("loanTerms.estimated", "Estimated")}
            </span>
          </div>

          {additionalRenovationLoans.map((loan, index) => {
            const amount = firstDefined(
              loan?.approved_renovation_loan_amount,
              loan?.approved_total_loan_amount,
              loan?.amount,
            );

            const status = String(
              loan?.renovation_financing_status || "CONFIRMED",
            ).toUpperCase();

            const reference =
              loan?.case_reference ||
              loan?.lender_reference ||
              t(
                "loanTerms.additionalLoanNumber",
                "Additional loan {{number}}",
                { number: index + 1 },
              );

            return (
              <div
                key={loan?.id || loan?.loan_case_id || `additional-${index}`}
                className="hb-terms-funding-row"
              >
                <div className="hb-terms-funding-row__main">
                  <span>
                    {t(
                      "loanTerms.additionalRenovationLoan",
                      "Additional renovation loan",
                    )}
                  </span>

                  <small>{reference}</small>

                  <strong>{formatEuro(amount)}</strong>
                </div>

                <span
                  className={`hb-funding-status ${
                    status === "CONFIRMED"
                      ? "hb-funding-status--confirmed"
                      : "hb-funding-status--estimated"
                  }`}
                >
                  {status === "CONFIRMED"
                    ? t("loanTerms.confirmed", "Confirmed")
                    : t("loanTerms.estimated", "Estimated")}
                </span>
              </div>
            );
          })}
        </div>

        <div className="hb-terms-section__total">
          <div>
            <span>
              {t("loanTerms.totalLenderLoan", "Total lender financing")}
            </span>
          </div>

          <strong>{formatEuro(combinedApprovedTotalLoan)}</strong>
        </div>
      </section>

      {/* ===================================================
          RENOVATION FINANCING STATUS
          =================================================== */}

      <div
        className={`hb-renovation-status ${
          isRenovationConfirmed
            ? "hb-renovation-status--confirmed"
            : "hb-renovation-status--estimated"
        }`}
      >
        <div className="hb-renovation-status__top">
          <div>
            <span>
              {t("loanTerms.renovationFinancingStatus", "Renovation financing")}
            </span>

            <strong>
              {isRenovationConfirmed
                ? t("loanTerms.renovationConfirmedTitle", "Financing confirmed")
                : t(
                    "loanTerms.renovationEstimatedTitle",
                    "Financing currently estimated",
                  )}
            </strong>
          </div>

          <span
            className={`hb-funding-status ${
              isRenovationConfirmed
                ? "hb-funding-status--confirmed"
                : "hb-funding-status--estimated"
            }`}
          >
            {isRenovationConfirmed
              ? t("loanTerms.confirmed", "Confirmed")
              : t("loanTerms.estimated", "Estimated")}
          </span>
        </div>

        <p>
          {isRenovationConfirmed
            ? t(
                "loanTerms.renovationConfirmedText",
                "The lender has confirmed the renovation financing after reviewing the available renovation information.",
              )
            : t(
                "loanTerms.renovationEstimatedText",
                "The renovation amount is preliminary and may be updated by the lender when the detailed renovation plan becomes available.",
              )}
        </p>

        {renovationFinancingNotes ? (
          <div className="hb-renovation-status__note">
            <span>{t("loanTerms.renovationNotes", "Lender note")}</span>

            <p>{renovationFinancingNotes}</p>
          </div>
        ) : null}
      </div>

      {/* ===================================================
          RENOVATION FUNDING BREAKDOWN
          =================================================== */}

      <section className="hb-terms-section hb-terms-section--renovation">
        <div className="hb-terms-section__header">
          <div>
            <span>
              {t("loanTerms.renovationFundingOverview", "Renovation funding")}
            </span>

            <small>
              {t(
                "loanTerms.renovationFundingSubtitle",
                "Funding allocated specifically to the renovation works",
              )}
            </small>
          </div>
        </div>

        <div className="hb-terms-section__rows">
          {/* LENDER RENOVATION LOAN */}

          <div className="hb-terms-funding-row">
            <div className="hb-terms-funding-row__main">
              <span>
                {additionalRenovationLoans.length
                  ? t(
                      "loanTerms.initialLenderRenovationLoan",
                      "Initial lender renovation loan",
                    )
                  : t(
                      "loanTerms.lenderRenovationLoan",
                      "Lender renovation loan",
                    )}
              </span>

              <small>
                {t("loanTerms.providedByLender", "Provided by the lender")}
              </small>

              <strong>{formatEuro(approvedRenovationLoanAmount)}</strong>
            </div>

            <span
              className={`hb-funding-status ${
                isRenovationConfirmed
                  ? "hb-funding-status--confirmed"
                  : "hb-funding-status--estimated"
              }`}
            >
              {isRenovationConfirmed
                ? t("loanTerms.confirmed", "Confirmed")
                : t("loanTerms.estimated", "Estimated")}
            </span>
          </div>

          {/* ADDITIONAL LENDER RENOVATION LOANS */}

          {additionalRenovationLoans.map((loan, index) => {
            const amount = firstDefined(
              loan?.approved_renovation_loan_amount,
              loan?.approved_total_loan_amount,
              loan?.amount,
            );

            const status = String(
              loan?.renovation_financing_status || "CONFIRMED",
            ).toUpperCase();

            return (
              <div
                key={`renovation-additional-${
                  loan?.id || loan?.loan_case_id || index
                }`}
                className="hb-terms-funding-row"
              >
                <div className="hb-terms-funding-row__main">
                  <span>
                    {t(
                      "loanTerms.additionalRenovationLoan",
                      "Additional renovation loan",
                    )}
                  </span>

                  <small>
                    {loan?.case_reference ||
                      t("loanTerms.providedByLender", "Provided by the lender")}
                  </small>

                  <strong>{formatEuro(amount)}</strong>
                </div>

                <span
                  className={`hb-funding-status ${
                    status === "CONFIRMED"
                      ? "hb-funding-status--confirmed"
                      : "hb-funding-status--estimated"
                  }`}
                >
                  {status === "CONFIRMED"
                    ? t("loanTerms.confirmed", "Confirmed")
                    : t("loanTerms.estimated", "Estimated")}
                </span>
              </div>
            );
          })}

          {/* SUPPLEMENTARY FUNDING */}

          {additionalRenovationFunding.map((source, index) => {
            const fundingStatus = String(source?.status || "").toUpperCase();

            const sourceType = getFundingTypeLabel(source?.source_type, t);

            return (
              <div
                key={
                  source.id || `${source.source_type}-${source.name}-${index}`
                }
                className="hb-terms-funding-row"
              >
                <div className="hb-terms-funding-row__main">
                  <span>
                    {source.name ||
                      t(
                        "loanTerms.additionalRenovationSupport",
                        "Additional renovation funding",
                      )}
                  </span>

                  <small>{sourceType}</small>

                  <strong>{formatEuro(source.amount)}</strong>
                </div>

                <span
                  className={`hb-funding-status ${
                    fundingStatus === "CONFIRMED"
                      ? "hb-funding-status--confirmed"
                      : "hb-funding-status--expected"
                  }`}
                >
                  {getFundingStatusLabel(fundingStatus, t)}
                </span>
              </div>
            );
          })}
        </div>

        {/* TOTAL */}

        <div className="hb-terms-section__total hb-terms-section__total--renovation">
          <div>
            <span>
              {hasUnconfirmedRenovationFunding
                ? t(
                    "loanTerms.potentialRenovationFunding",
                    "Potential renovation funding",
                  )
                : t(
                    "loanTerms.totalRenovationFunding",
                    "Total renovation funding",
                  )}
            </span>

            {hasUnconfirmedRenovationFunding ? (
              <small>
                {t(
                  "loanTerms.confirmedRenovationFunding",
                  "Currently confirmed",
                )}
                : {formatEuro(confirmedRenovationFunding)}
              </small>
            ) : (
              <small>
                {t(
                  "loanTerms.allRenovationFundingConfirmed",
                  "All listed renovation funding is confirmed",
                )}
              </small>
            )}
          </div>

          <strong>{formatEuro(potentialRenovationFunding)}</strong>
        </div>
      </section>

      {/* ===================================================
    INITIAL LOAN TERMS
    =================================================== */}

      <div className="hb-loan-terms-group">
        <div className="hb-loan-terms-group__header">
          <div>
            <span className="hb-loan-terms-group__eyebrow">
              {t("loanTerms.initialLoanTerms", "Initial loan terms")}
            </span>

            <small>
              {t(
                "loanTerms.initialLoanTermsSubtitle",
                "Terms of the original approved lender loan",
              )}
            </small>
          </div>
        </div>

        <div className="hb-terms-list">
          <div className="hb-terms-item">
            <span>{t("loanTerms.loanTerm", "Financing term")}</span>

            <strong>{formatYears(tenureYears, t)}</strong>
          </div>

          <div className="hb-terms-item">
            <span>{t("loanTerms.estimatedAPR", "Indicative APR")}</span>

            <strong>{formatPercent(indicativeApr)}</strong>
          </div>

          <div className="hb-terms-item">
            <span>{t("loanTerms.ownContribution", "Own contribution")}</span>

            <strong>{formatEuro(ownContribution)}</strong>
          </div>
        </div>
      </div>

      {/* ===================================================
    ADDITIONAL LOAN TERMS
    =================================================== */}

      {additionalRenovationLoans.length > 0 ? (
        <div className="hb-additional-terms">
          <div className="hb-additional-terms__heading">
            <span>
              {t(
                "loanTerms.additionalLoanTerms",
                "Additional renovation loan terms",
              )}
            </span>

            <small>
              {t(
                "loanTerms.additionalLoanTermsSubtitle",
                "Separate financing terms for additional renovation loans",
              )}
            </small>
          </div>

          {additionalRenovationLoans.map((additionalLoan, index) => {
            const amount =
              additionalLoan?.approved_renovation_loan_amount ??
              additionalLoan?.approved_total_loan_amount;

            return (
              <div
                key={additionalLoan.loan_case_id || additionalLoan.id || index}
                className="hb-additional-terms__loan"
              >
                <div className="hb-additional-terms__loan-header">
                  <div>
                    <strong>
                      {t(
                        "loanTerms.additionalRenovationLoan",
                        "Additional renovation loan",
                      )}
                    </strong>

                    {additionalLoan.case_reference ? (
                      <small>{additionalLoan.case_reference}</small>
                    ) : null}
                  </div>

                  <span className="hb-funding-status hb-funding-status--confirmed">
                    {t("loanTerms.confirmed", "Confirmed")}
                  </span>
                </div>

                <div className="hb-additional-terms__grid">
                  <div className="hb-terms-item">
                    <span>{t("loanTerms.loanAmount", "Loan amount")}</span>

                    <strong>{formatEuro(amount)}</strong>
                  </div>

                  <div className="hb-terms-item">
                    <span>{t("loanTerms.loanTerm", "Financing term")}</span>

                    <strong>
                      {formatYears(additionalLoan.tenure_years, t)}
                    </strong>
                  </div>

                  <div className="hb-terms-item">
                    <span>{t("loanTerms.estimatedAPR", "Indicative APR")}</span>

                    <strong>
                      {formatPercent(additionalLoan.indicative_apr_pct)}
                    </strong>
                  </div>

                  <div className="hb-terms-item">
                    <span>
                      {t(
                        "loanTerms.monthlyPayment",
                        "Estimated monthly payment",
                      )}
                    </span>

                    <strong>
                      {formatEuro(additionalLoan.estimated_monthly_payment)}
                    </strong>
                  </div>

                  <div className="hb-terms-item">
                    <span>
                      {t("loanTerms.loanStartDate", "Loan start date")}
                    </span>

                    <strong>
                      {additionalLoan.loan_start_date
                        ? new Date(
                            `${additionalLoan.loan_start_date}T00:00:00`,
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </strong>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* ===================================================
          NOTE
          =================================================== */}

      <p className="hb-summary-note">
        {hasUnconfirmedRenovationFunding
          ? t(
              "loanTerms.approvedWithExpectedSupportNote",
              "The lender decision is approved. Renovation funding marked as estimated or expected may still change before it is confirmed.",
            )
          : t(
              "loanTerms.approvedNote",
              "These financing terms were recorded by your lender as part of the final loan decision.",
            )}
      </p>
    </div>
  );
}
